import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { ACCESS_TIERS, KAJABI, STUDIO_BG } from "@/lib/data";
import Testimonials from "@/components/Testimonials";
import FAQ from "@/components/FAQ";
import StatsMarquee from "@/components/StatsMarquee";

export default function Access() {
 return (
 <div data-testid="access-page">
 {/* Hero */}
 <section
 data-testid="access-hero"
 className="relative min-h-[70vh] overflow-hidden grain border-b border-ink-600"
 >
 <div
 className="absolute inset-0 bg-cover bg-center"
 style={{ backgroundImage: `url(${STUDIO_BG})` }}
 />
 <div className="absolute inset-0 bg-gradient-to-b from-ink-900/85 via-ink-900/80 to-ink-900" />
 <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-36 md:pt-44 pb-20">
 <motion.div
 initial={{ opacity: 0, y: 16 }}
 animate={{ opacity: 1, y: 0 }}
 transition={{ duration: 0.6 }}
 className="max-w-4xl"
 >
 <p className="tag-kicker">Monthly Membership</p>
 <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.1] tracking-normal mt-5">
 Underground <span className="text-amber italic">ACCESS.</span>
 </h1>
 <p className="mt-6 text-xl md:text-2xl text-white/75 max-w-2xl leading-snug">
 Weekly immersion into the legit world of song creation, creative
 critique, sync licensing and music publishing.
 </p>
 <div className="mt-8 flex items-center gap-5 flex-wrap">
 <a
 href="#tiers"
 data-testid="access-hero-cta"
 className="inline-flex items-center gap-3 bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-sm px-7 py-4 rounded-sm transition-all"
 >
 See Pricing <ArrowRight size={16} />
 </a>
 <p className="text-sm text-white/55 font-mono">
 From $39/mo · 6-month commitment
 </p>
 </div>
 </motion.div>
 </div>
 </section>

 <StatsMarquee />

 {/* Pricing */}
 <section
 data-testid="access-tiers"
 id="tiers"
 className="relative bg-ink-900 py-24 md:py-32"
 >
 <div className="max-w-7xl mx-auto px-4 md:px-8">
 <div className="mb-14 max-w-3xl">
 <p className="tag-kicker">Three tiers · one community</p>
 <h2 className="font-display text-4xl md:text-5xl font-bold tracking-normal leading-[1.15] mt-3">
 Pick your depth of access.
 </h2>
 <p className="text-white/65 mt-5 text-lg">
 ACCESS PLUS and PREMIUM include the live weekly room, the
 community, and executive education. The higher tiers add deeper
 feedback, active briefs and representation opportunities.
 </p>
 </div>

 <div className="grid md:grid-cols-3 gap-5">
 {ACCESS_TIERS.map((tier, i) => (
 <motion.div
 key={tier.name}
 initial={{ opacity: 0, y: 24 }}
 whileInView={{ opacity: 1, y: 0 }}
 viewport={{ once: true }}
 transition={{ duration: 0.5, delay: i * 0.1 }}
 data-testid={`tier-${tier.name.toLowerCase().replace(" ", "-")}`}
 className={`relative p-8 md:p-10 flex flex-col min-h-[580px] border transition-all ${
 tier.featured
 ? "bg-amber text-black border-amber"
 : "bg-ink-800 border-ink-600 hover:border-amber/50"
 }`}
 >
 {tier.featured && (
 <span className="absolute -top-3 left-8 bg-black text-amber text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-sm font-mono">
 Most popular
 </span>
 )}
 <p
 className={`tag-kicker ${
 tier.featured ? "!text-black/60" : ""
 }`}
 >
 {tier.name}
 </p>
 <div className="mt-6 flex items-baseline gap-2">
 <span
 className={`font-display text-6xl font-bold ${
 tier.featured ? "text-black" : ""
 }`}
 >
 {tier.price}
 </span>
 <span
 className={`text-sm font-mono ${
 tier.featured ? "text-black/60" : "text-white/50"
 }`}
 >
 {tier.period}
 </span>
 </div>
 <p
 className={`mt-2 text-base ${
 tier.featured ? "text-black/80" : "text-white/75"
 }`}
 >
 {tier.tagline}
 </p>

 <ul className="mt-8 space-y-4 flex-1">
 {tier.features.map((f) => (
 <li key={f} className="flex items-start gap-3">
 <span
 className={`mt-1 flex-shrink-0 ${
 tier.featured ? "text-black" : "text-amber"
 }`}
 >
 <Check size={16} strokeWidth={2.5} />
 </span>
 <span
 className={`text-sm leading-relaxed ${
 tier.featured ? "text-black/85" : "text-white/80"
 }`}
 >
 {f}
 </span>
 </li>
 ))}
 </ul>

 <a
 href={tier.checkoutUrl || KAJABI.access}
 target="_blank"
 rel="noopener noreferrer"
 data-testid={`tier-cta-${tier.name.toLowerCase().replace(" ", "-")}`}
 className={`mt-10 inline-flex items-center justify-center gap-2 font-bold uppercase tracking-wider text-xs px-6 py-4 rounded-sm transition-colors ${
 tier.featured
 ? "bg-black text-amber hover:bg-ink-900"
 : "bg-white text-black hover:bg-amber"
 }`}
 >
 Join {tier.name}
 <ArrowRight size={14} />
 </a>
 </motion.div>
 ))}
 </div>
 </div>
 </section>

 {/* What you get */}
 <section
 data-testid="access-details"
 className="relative bg-ink-800 py-24 md:py-32 border-y border-ink-600"
 >
 <div className="max-w-7xl mx-auto px-4 md:px-8 grid md:grid-cols-12 gap-12">
 <div className="md:col-span-5">
 <p className="tag-kicker">Inside the room</p>
 <h2 className="font-display text-4xl md:text-5xl font-bold tracking-normal leading-[1.15] mt-3">
 A weekly hour that changes how you write.
 </h2>
 <p className="mt-6 text-white/65 text-lg">
 Live group coaching. Real briefs. Real songs getting critiqued
 in real time.
 </p>
 </div>
 <ul className="md:col-span-7 grid sm:grid-cols-2 gap-4">
 {[
 "Weekly 1-hour live session. Every Thursday (PLUS & PREMIUM)",
 "Live song critiques from Devon, Becki and Team inside each session",
 "Executive-level education inside each session",
 "Recorded replays sent if you miss a meeting (PLUS & PREMIUM)",
 "5–10 active briefs",
 "Potential representation for submitted songs",
 "Private online UM community",
 "Access to select pre-recorded coaching",
 "Discounts on C.A.M.P., mini-courses and the e-book",
 ].map((f, i) => (
 <li
 key={i}
 className="flex items-start gap-3 p-4 border border-ink-600 bg-ink-900"
 >
 <span className="text-amber font-mono text-sm mt-0.5">
 {String(i + 1).padStart(2, "0")}
 </span>
 <span className="text-sm text-white/80 leading-relaxed">
 {f}
 </span>
 </li>
 ))}
 </ul>
 </div>
 </section>

 <Testimonials />
 <FAQ />

 {/* Final CTA */}
 <section
 data-testid="access-final-cta"
 className="relative bg-ink-900 py-24 md:py-32 border-t border-ink-600"
 >
 <div className="max-w-4xl mx-auto text-center px-4 md:px-8">
 <p className="tag-kicker">Your move</p>
 <h2 className="font-display text-4xl md:text-6xl font-bold tracking-normal leading-[1.15] mt-5">
 Six months. One real shot at sync.
 </h2>
 <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
 A 6-month commitment to community, collaboration, and real
 feedback. Because that's what it takes to actually shift your
 craft and your catalog. No ownership of your masters. No hidden
 back-end.
 </p>
 <a
 href={KAJABI.access}
 target="_blank"
 rel="noopener noreferrer"
 data-testid="access-final-cta-link"
 className="mt-10 inline-flex items-center gap-3 bg-amber hover:bg-amber-hover text-black font-bold uppercase tracking-widest text-sm px-8 py-4 rounded-sm transition-all group"
 >
 Join Underground ACCESS
 <ArrowRight
 size={16}
 className="transition-transform group-hover:translate-x-1"
 />
 </a>
 </div>
 </section>
 </div>
 );
}
